/**
 * Coach authentication — Supabase magic-link only.
 *
 * The portal is a browser-only app, so the real security boundary is the
 * database (RLS). This module makes the browser PROVE it's an allowed coach
 * before the database will return any data:
 *
 *   - No session            → the login gate is shown, app never renders.
 *   - Valid magic-link login → Supabase issues an `authenticated` JWT; RLS
 *                              policies (FOR ... TO authenticated) let the
 *                              app read/write again.
 *
 * Only accounts pre-created in the Supabase dashboard can ever log in:
 *   1. Public sign-ups are disabled in Supabase (Authentication settings).
 *   2. `shouldCreateUser: false` below — belt-and-suspenders, so even if (1)
 *      were ever flipped on, requesting a link for an unknown email does
 *      nothing instead of silently provisioning a stranger.
 *
 * To add a coach: Supabase dashboard → Authentication → Users → Add user
 * (enter their email). Nothing to change in code.
 */

import { supabase } from '../db/supabase.js';

/** Current session, or null if not logged in. */
export async function getSession() {
  const { data } = await supabase.auth.getSession();
  return data.session;
}

/**
 * Subscribe to auth state. Fires immediately with the initial session
 * (supabase-js emits an INITIAL_SESSION event on subscribe), then on every
 * sign-in / sign-out / token refresh.
 */
export function onAuthChange(handler) {
  supabase.auth.onAuthStateChange((_event, session) => handler(session));
}

/** Sign out, then hard-reload to guarantee a clean (gated) state. */
export async function signOut() {
  await supabase.auth.signOut();
  location.reload();
}

/**
 * Render the full-screen login gate into `mount` (the <app-root>).
 * Replaces all content — the app is not in the DOM until a session exists.
 */
export function renderLogin(mount) {
  mount.innerHTML = /* html */ `
    <div class="auth-gate">
      <form class="auth-card" id="auth-form" novalidate>
        <div class="auth-card__brand">
          <span class="auth-card__logo">TAP Athletes</span>
          <span class="auth-card__badge">Portal</span>
        </div>
        <h1 class="auth-card__title">Coach sign in</h1>
        <p class="auth-card__sub">
          Enter your coach email and we'll send you a one-time login link.
          Access is limited to approved coaches.
        </p>

        <label class="auth-card__label" for="auth-email">Email</label>
        <input
          class="auth-card__input"
          id="auth-email"
          type="email"
          name="email"
          autocomplete="email"
          inputmode="email"
          placeholder="you@tapathletes.com"
          required
        />

        <button class="auth-card__btn" type="submit" id="auth-submit">
          Send login link
        </button>

        <p class="auth-card__msg" id="auth-msg" role="status" aria-live="polite"></p>
      </form>
      <p class="auth-gate__foot">TAP Athletes — internal use only.</p>
    </div>
  `;

  injectAuthStyles();

  const form = mount.querySelector('#auth-form');
  const emailInput = mount.querySelector('#auth-email');
  const submitBtn = mount.querySelector('#auth-submit');
  const msg = mount.querySelector('#auth-msg');

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = emailInput.value.trim();
    if (!email) {
      setMsg(msg, 'Enter your email first.', 'error');
      return;
    }

    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending…';
    setMsg(msg, '', null);

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        // Only pre-created coaches can log in — never auto-create accounts.
        shouldCreateUser: false,
        // Send the user back to whatever origin they're on (prod or dev).
        emailRedirectTo: location.origin,
      },
    });

    submitBtn.disabled = false;
    submitBtn.textContent = 'Send login link';

    if (error) {
      // Supabase returns an error for unknown emails when shouldCreateUser is
      // false. Keep the message generic so it isn't an account-enumeration oracle.
      setMsg(
        msg,
        "If that email is an approved coach, a login link is on its way. Check your inbox.",
        'ok'
      );
      return;
    }

    setMsg(
      msg,
      `Login link sent to ${email}. Open it on this device to finish signing in.`,
      'ok'
    );
  });
}

function setMsg(el, text, kind) {
  el.textContent = text;
  el.dataset.kind = kind ?? '';
}

/** Login-gate styles. Tokens only — no raw hex/px (per CLAUDE.md). */
function injectAuthStyles() {
  if (document.getElementById('auth-styles')) return;
  const style = document.createElement('style');
  style.id = 'auth-styles';
  style.textContent = /* css */ `
    .auth-gate {
      min-height: 100dvh;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: var(--space-sm);
      padding: var(--space-sm) var(--container-pad-sm);
      background: var(--color-bg);
    }
    .auth-card {
      width: 100%;
      max-width: 26rem;
      display: flex;
      flex-direction: column;
      gap: var(--space-2xs);
      padding: var(--space-sm);
      background: var(--color-bg-card);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-lg);
    }
    .auth-card__brand {
      display: flex;
      align-items: center;
      gap: var(--space-3xs);
      margin-bottom: var(--space-3xs);
    }
    .auth-card__logo {
      font-family: var(--font-display);
      font-weight: var(--weight-extrabold);
      letter-spacing: var(--tracking-tight);
      font-size: var(--text-h3);
      color: var(--color-text);
    }
    .auth-card__badge {
      font-size: var(--text-caption);
      letter-spacing: var(--tracking-eyebrow);
      text-transform: uppercase;
      color: var(--color-text-muted);
      border: 1px solid var(--color-border-strong);
      border-radius: var(--radius-sm);
      padding: var(--space-4xs) var(--space-3xs);
    }
    .auth-card__title {
      font-family: var(--font-display);
      font-weight: var(--weight-bold);
      font-size: var(--text-h2);
      line-height: var(--leading-tight);
      color: var(--color-text);
      margin: 0;
    }
    .auth-card__sub {
      font-size: var(--text-body);
      line-height: var(--leading-snug);
      color: var(--color-text-muted);
      margin: 0 0 var(--space-3xs);
    }
    .auth-card__label {
      font-size: var(--text-caption);
      letter-spacing: var(--tracking-eyebrow);
      text-transform: uppercase;
      color: var(--color-text-faint);
    }
    .auth-card__input {
      width: 100%;
      min-height: var(--tap-min);
      padding: 0 var(--space-2xs);
      font-size: var(--text-body-lead);
      font-family: var(--font-sans);
      color: var(--color-text);
      background: var(--color-bg);
      border: 1px solid var(--color-border-strong);
      border-radius: var(--radius-md);
      transition: border-color var(--duration-fast) var(--ease-out);
    }
    .auth-card__input:focus {
      outline: none;
      border-color: var(--color-cta);
    }
    .auth-card__btn {
      min-height: var(--tap-min);
      margin-top: var(--space-3xs);
      font-family: var(--font-sans);
      font-weight: var(--weight-semibold);
      font-size: var(--text-body-lead);
      color: var(--white);
      background: var(--color-cta);
      border: none;
      border-radius: var(--radius-md);
      cursor: pointer;
      transition: background var(--duration-fast) var(--ease-out);
    }
    .auth-card__btn:hover:not(:disabled) { background: var(--color-cta-hover); }
    .auth-card__btn:disabled { opacity: 0.6; cursor: progress; }
    .auth-card__msg {
      font-size: var(--text-body);
      line-height: var(--leading-snug);
      margin: var(--space-3xs) 0 0;
      min-height: 1.2em;
    }
    .auth-card__msg[data-kind="ok"]    { color: var(--color-success); }
    .auth-card__msg[data-kind="error"] { color: var(--color-danger); }
    .auth-gate__foot {
      font-size: var(--text-caption);
      color: var(--color-text-faint);
      margin: 0;
    }
  `;
  document.head.appendChild(style);
}
